import React, { useState, useContext } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Button from '@components/buttons';
import Input from '@components/form/Input';
import Label from '@components/form/Label';
import Radio from '@components/form/radio/Radio';
import TextArea from '@components/form/TextArea';
import { Box, Flex } from '@components/layout/Grid';
import { snackbarVar } from '@lib/apollo/policies';
import { H4 } from '@components/typography/Headings';
import { P } from '@components/typography/Paragraph';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import Banner from '@organisms/Banner';
import { Body, Footer, Header, Modal } from './base';
import {
  Mutation,
  UserPlaylistsDocument,
  useUserPlaylistsLazyQuery,
} from '@gql/generated';
import { UserContext } from '@lib/contexts/UserProvider';
import AnalyticsManager from '@lib/analytics/manager';

const PATCH_PLAYLIST = gql`
  mutation patchPlaylist(
    $id: ID!
    $title: String!
    $visibility: String
    $description: String
  ) {
    patchPlaylist(
      id: $id
      title: $title
      visibility: $visibility
      description: $description
    ) {
      playlist {
        id
        title
        visibility
        description
      }
    }
  }
`;

const STORE_PLAYLIST = gql`
  mutation addPlaylist(
    $title: String!
    $visibility: String
    $description: String
  ) {
    addPlaylist(
      title: $title
      visibility: $visibility
      description: $description
    ) {
      playlist {
        id
      }
    }
  }
`;

// TODO: convert this into hook style component
const PlaylistModal = props => {
  const { query } = useRouter();
  const [title, setTitle] = useState(props.playlist?.title || '');
  const [description, setDescription] = useState(
    props.playlist?.description || ''
  );
  const [visibility, setVisibility] = useState(
    props.playlist?.visibility || 'hidden'
  );
  const [open, toggleModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUser] = useContext(UserContext);
  const [_, { refetch }] = useUserPlaylistsLazyQuery();
  const [addPlaylistMutation] = useMutation(STORE_PLAYLIST);
  const [patchPlaylistMutation] = useMutation<{
    patchPlaylist: Mutation['patchPlaylist'];
  }>(PATCH_PLAYLIST);

  const handleOpenModal = () => {
    toggleModal(true);
  };

  const handleCloseModal = () => {
    toggleModal(false);
    props.onModalClose?.();
  };

  const storePlaylist = () => {
    return addPlaylistMutation({
      variables: {
        title: String(title).trim(),
        visibility: visibility,
        description: String(description).trim(),
      },
    }).then(async response => {
      await refetch({ page: 1, perPage: 12 });
      // PLAYLIST TODO: handle errors
      const playlistID = response.data.addPlaylist.playlist.id;
      await props.onCompletedStorePlaylist(playlistID);
      AnalyticsManager().sendPlaylistCreatedEvent({ playlist_id: playlistID });
      snackbarVar({
        isSnackBarOpen: true,
        goTo: 'PLAYLIST',
        id: playlistID,
        snackBarMessage: t`snackbars.created_playlist`,
      });
      handleCloseModal();
    });
  };

  const patchPlaylist = () => {
    return patchPlaylistMutation({
      variables: {
        id: props.playlist.id,
        title: title,
        visibility: visibility,
        description: description,
      },
    }).then(() => {
      // PLAYLIST TODO: handle errors
      handleCloseModal();
      snackbarVar({
        isSnackBarOpen: true,
        goTo: 'PLAYLIST',
        snackBarMessage: t`snackbars.updated_playlist`,
        id: props.playlist.id,
      });
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    if (props.playlist) {
      await patchPlaylist();
    } else {
      await storePlaylist();
    }
    setLoading(false);
  };

  const isFormValid =
    title.length < 2 || title.length > 40 || description.length > 400;

  return (
    <React.Fragment>
      <Modal
        open={open}
        handleClose={handleCloseModal}
        data-testid="Modal--CreatePlaylist"
      >
        <form onSubmit={handleSubmit}>
          <Header handleClose={handleCloseModal}>
            <Box as={H4} color="#242424" mt="0px" mb="0px">
              {visibility === 'public' && (
                <Trans
                  id="createplaylist_modal.public_title"
                  message={'Public Playlist'}
                />
              )}
              {visibility === 'hidden' && (
                <Trans
                  id="createplaylist_modal.hidden_title"
                  message={'Hidden Playlist'}
                />
              )}
              {visibility === 'private' && (
                <Trans
                  id="createplaylist_modal.private_title"
                  message={'Private Playlist'}
                />
              )}
            </Box>
          </Header>
          <Body>
            <Box mb="10px">
              <Box mb="7px">
                <Label htmlFor={'title'}>
                  <Trans
                    id="createplaylist_modal.name.label"
                    message={'Name'}
                  />
                </Label>
              </Box>
              <Input
                id="title"
                placeholder={t`createplaylist_modal.name.placeholder`}
                value={title}
                onChange={value => {
                  setTitle(String(value).trim());
                }}
                required
                minLength={2}
                maxLength={40}
              />
            </Box>
            <Box mb="10px">
              <Flex>
                <Box mr={30}>
                  <Radio
                    label={
                      <Trans
                        id="createplaylist_modal.options.one"
                        message={'Public'}
                      />
                    }
                    isChecked={visibility === 'public'}
                    handleChange={() => {
                      setVisibility('public');
                    }}
                  />
                </Box>
                <Box mr={30}>
                  <Radio
                    label={
                      <Trans
                        id="createplaylist_modal.options.two"
                        message="Hidden"
                      />
                    }
                    isChecked={visibility === 'hidden'}
                    handleChange={() => {
                      setVisibility('hidden');
                    }}
                  />
                </Box>
                <Box mr={30}>
                  <Radio
                    label={
                      <Trans
                        id="createplaylist_modal.options.three"
                        message="Private"
                      />
                    }
                    isChecked={visibility === 'private'}
                    handleChange={() => {
                      setVisibility('private');
                    }}
                  />
                </Box>
              </Flex>
            </Box>
            <Box mb="10px">
              <P color="#242424">
                {visibility === 'public' && (
                  <Trans
                    id="createplaylist_modal.public_helper"
                    message={
                      'Your playlist gets shown in public listings and may be promoted to other users.'
                    }
                  />
                )}
                {visibility === 'hidden' && (
                  <Trans
                    id="createplaylist_modal.hidden_helper"
                    message={'Your playlist is hidden.'}
                  />
                )}
                {visibility === 'private' && (
                  <Trans
                    id="createplaylist_modal.private_helper"
                    message={'Your playlist is private.'}
                  />
                )}
              </P>
            </Box>
            {currentUser?.privacySettings?.isPrivateProfile &&
            visibility === 'public' ? (
              <Box my={'10px'}>
                <Banner
                  style={{
                    backgroundColor: '#FFE9CB',
                  }}
                  iconColor={'#242424'}
                  noMargin
                >
                  <Trans
                    id="createplaylist_modal.privacy_helper"
                    message={
                      'Your profile is currently set to private but this playlist will still be visible to the public. <0>Edit Privacy Settings.</0>'
                    }
                    components={[
                      <Link
                        key="privacy"
                        href={`/[lang]/me/settings/[pageId]`}
                        as={`/${query.lang}/me/settings/privacy-settings`}
                      >

                      </Link>,
                    ]}
                  />
                </Banner>
              </Box>
            ) : null}
            <Box mb="10px">
              <Box mb="7px">
                <Label htmlFor={'description'}>
                  <Trans
                    id="createplaylist_modal.description.label"
                    message={'Description'}
                  />
                </Label>
              </Box>
              <TextArea
                id={'description'}
                placeholder={t`createplaylist_modal.description.placeholder`}
                value={description}
                maxLength={400}
                onChange={e => {
                  setDescription(e.target.value);
                }}
              />
            </Box>
          </Body>
          <Footer>
            <Button appearance="link" type="reset" onClick={handleCloseModal}>
              <Trans
                id="createplaylist_modal.actions.secondary"
                message={'CANCEL'}
              />
            </Button>
            <Button loading={loading} disabled={isFormValid} type="submit">
              <Trans
                id="createplaylist_modal.actions.primary"
                message={'SAVE'}
              />
            </Button>
          </Footer>
        </form>
      </Modal>
      {props.children(handleOpenModal)}
    </React.Fragment>
  );
};

export default PlaylistModal;
